/**
 * TicketChatInput — text input, attachment picker, and send button for ticket chat.
 *
 * Attachments upload as soon as they're picked (see `useChatAttachments`), so
 * Send only waits on the message POST. Send is enabled once there's either text
 * or at least one uploaded file, and stays disabled while an upload is in flight.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import {
    Modal,
    Platform,
    Pressable,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { TicketAttachmentTray } from "./TicketAttachmentTray";

import { useChatAttachments, type AttachmentSource } from "@/hooks/useChatAttachments";
import { colors, fonts, radius, spacing } from "@/lib/theme";
import type { Attachment } from "@/lib/ticket-api";

interface Props {
    ticketId: number;
    onSend: (text: string, attachments: Attachment[]) => void;
    disabled?: boolean;
    placeholder?: string;
}

const SOURCES: Array<{ key: AttachmentSource; icon: string; label: string }> = [
    { key: "camera", icon: "camera-outline", label: "Take Photo" },
    { key: "library", icon: "image-multiple-outline", label: "Choose Photos" },
    { key: "document", icon: "file-document-outline", label: "Choose Document" }
];

export function TicketChatInput({ ticketId, onSend, disabled = false, placeholder }: Props) {
    const [text, setText] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);
    const insets = useSafeAreaInsets();
    const attachments = useChatAttachments(ticketId);

    const handleSend = () => {
        const trimmed = text.trim();
        const ready = attachments.toAttachments();
        if ((!trimmed && ready.length === 0) || disabled || attachments.hasPending) {
            return;
        }
        onSend(trimmed, ready);
        setText("");
        attachments.clear();
    };

    const handlePick = async (source: AttachmentSource) => {
        setSheetOpen(false);
        await attachments.pick(source);
    };

    const canSend =
        (text.trim().length > 0 || attachments.readyCount > 0) &&
        !disabled &&
        !attachments.hasPending;

    return (
        <View
            style={[
                styles.container,
                {
                    paddingBottom:
                        Platform.OS === "android" ? spacing.md : Math.max(insets.bottom, spacing.sm)
                }
            ]}
        >
            <TicketAttachmentTray
                items={attachments.staged}
                onRemove={attachments.remove}
                onRetry={attachments.retry}
            />

            {attachments.pickerError && (
                <Text style={styles.errorText}>{attachments.pickerError}</Text>
            )}

            <View style={styles.row}>
                <TouchableOpacity
                    style={styles.attachBtn}
                    onPress={() => setSheetOpen(true)}
                    disabled={disabled}
                    activeOpacity={0.7}
                    accessibilityLabel="Add attachment"
                >
                    <MaterialCommunityIcons
                        name="paperclip"
                        size={22}
                        color={disabled ? colors.textTertiary : colors.textSecondary}
                    />
                </TouchableOpacity>

                <View style={styles.inputWrapper}>
                    <TextInput
                        style={styles.input}
                        placeholder={placeholder ?? "Type a message..."}
                        placeholderTextColor={colors.textTertiary}
                        value={text}
                        onChangeText={setText}
                        editable={!disabled}
                        maxLength={2000}
                        multiline
                    />
                </View>

                <TouchableOpacity
                    style={[styles.sendBtn, canSend && styles.sendBtnActive]}
                    onPress={handleSend}
                    disabled={!canSend}
                    activeOpacity={0.7}
                    accessibilityLabel="Send message"
                >
                    <MaterialCommunityIcons
                        name="arrow-up"
                        size={20}
                        color={canSend ? colors.surface : colors.textTertiary}
                    />
                </TouchableOpacity>
            </View>

            <Modal
                visible={sheetOpen}
                transparent
                animationType="fade"
                onRequestClose={() => setSheetOpen(false)}
            >
                <Pressable style={styles.backdrop} onPress={() => setSheetOpen(false)}>
                    <Pressable
                        style={[
                            styles.sheet,
                            { paddingBottom: Math.max(insets.bottom, spacing.md) }
                        ]}
                        onPress={(e) => e.stopPropagation()}
                    >
                        {SOURCES.map((source) => (
                            <TouchableOpacity
                                key={source.key}
                                style={styles.sheetRow}
                                onPress={() => handlePick(source.key)}
                                activeOpacity={0.7}
                            >
                                <MaterialCommunityIcons
                                    name={source.icon as never}
                                    size={22}
                                    color={colors.teal600}
                                />
                                <Text style={styles.sheetLabel}>{source.label}</Text>
                            </TouchableOpacity>
                        ))}
                        <TouchableOpacity
                            style={[styles.sheetRow, styles.sheetCancel]}
                            onPress={() => setSheetOpen(false)}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.sheetCancelLabel}>Cancel</Text>
                        </TouchableOpacity>
                    </Pressable>
                </Pressable>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: colors.surface,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm
    },
    row: { flexDirection: "row", alignItems: "flex-end", gap: spacing.sm },
    attachBtn: {
        width: 36,
        height: 36,
        alignItems: "center",
        justifyContent: "center"
    },
    inputWrapper: {
        flex: 1,
        backgroundColor: colors.background,
        borderRadius: radius.lg,
        paddingHorizontal: spacing.md,
        paddingVertical: Platform.OS === "ios" ? spacing.sm : 0,
        maxHeight: 120
    },
    input: {
        fontFamily: fonts.regular,
        fontSize: 15,
        color: colors.textPrimary,
        maxHeight: 100
    },
    sendBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.gray200,
        alignItems: "center",
        justifyContent: "center"
    },
    sendBtnActive: { backgroundColor: colors.teal500 },
    errorText: {
        fontFamily: fonts.regular,
        fontSize: 12,
        color: colors.error,
        marginBottom: spacing.xs
    },
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(31, 21, 71, 0.35)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius.xl,
        borderTopRightRadius: radius.xl,
        paddingTop: spacing.sm,
        paddingHorizontal: spacing.md
    },
    sheetRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md
    },
    sheetLabel: {
        fontFamily: fonts.medium,
        fontSize: 16,
        color: colors.textPrimary
    },
    sheetCancel: {
        justifyContent: "center",
        borderTopWidth: 1,
        borderTopColor: colors.border,
        marginTop: spacing.xs
    },
    sheetCancelLabel: {
        fontFamily: fonts.medium,
        fontSize: 16,
        color: colors.textSecondary
    }
});
